import { useMemo } from 'react';

import { usePhotos } from './usePhotos';
import { useSearchParamsWithUpdate } from './useSearchParamsWithUpdate';
import { Photo } from '@/lib/Photo';
import { Photos } from '@/lib/Photos';

export function usePhotoFilter() {
  const { photos: photoData } = usePhotos();
  
  // Convert PhotoData to Photo instances
  const photoInstances = useMemo(() => photoData.map(data => Photo.fromApi(data)), [photoData]);
  const photos = useMemo(() => new Photos(photoInstances), [photoInstances]);
  const { searchParams, updateParams } = useSearchParamsWithUpdate();

  const state = searchParams.get('state') || '';
  const province = searchParams.get('province') || '';
  const region = searchParams.get('region') || '';
  const type = searchParams.get('type') || '';

  const states = useMemo(() => {
    return [''].concat(photos.states);
  }, [photos]);

  const names = useMemo(() => {
    return [''].concat(photos.names());
  }, [photos]);

  const provinces = useMemo(() => {
    return [''].concat(photos.provinces(state));
  }, [photos, state]);

  const regions = useMemo(() => {
    return [''].concat(photos.regions(state, province));
  }, [photos, state, province]);

  const types = useMemo(() => {
    return [''].concat(photos.types());
  }, [photos]);

  // Clear values "downstream"; not sure if that should be conditional on the value's validity
  const setState = (value: string) => updateParams({ state: value, province: null, region: null });
  const setProvince = (value: string) => updateParams({ province: value, region: null });
  const setRegion = (value: string) => updateParams({ region: value });
  const setType = (value: string) => updateParams({ type: value });
  const clearFilters = () => updateParams({ state: null, province: null, region: null, type: null });

  return {
    states,
    provinces,
    regions,
    types,
    names,
    state,
    province,
    region,
    type,
    setState,
    setProvince,
    setRegion,
    setType,
    clearFilters
  };
}
